import { useCallback, useRef, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { startRecording, stopRecording } from '../../services/voiceJobService';
import { userSafeErrorMessage } from '../../utils/error/userSafeError';

const PREVIEW_MS = 1500;

/**
 * Quick-log composer: voice → preview → silent submit; pattern/text submit silent.
 */
export default function useQuickLogCompose({
  role,
  locale,
  userName,
  detectContext,
  startStream,
  setMessages,
  setLoading,
  sendMessage,
  messagesRef,
  clearQuickLogPreviewTimersRef,
  setListRowLock,
}) {
  const intl = useIntl();
  const recorderRef = useRef(null);
  const intervalRef = useRef(null);
  const previewTimersRef = useRef(new Map());
  const stopVoiceFnRef = useRef(null);
  const [recordingMessageId, setRecordingMessageId] = useState(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const cancelPreviewTimer = useCallback((messageId) => {
    const t = previewTimersRef.current.get(messageId);
    if (t != null) clearTimeout(t);
    previewTimersRef.current.delete(messageId);
  }, []);

  const clearAllPreviewTimers = useCallback(() => {
    previewTimersRef.current.forEach((tid) => clearTimeout(tid));
    previewTimersRef.current.clear();
  }, []);

  useEffect(() => {
    clearQuickLogPreviewTimersRef.current = clearAllPreviewTimers;
    return () => {
      clearAllPreviewTimers();
      clearQuickLogPreviewTimersRef.current = () => {};
    };
  }, [clearAllPreviewTimers, clearQuickLogPreviewTimersRef]);

  const cancelVoice = useCallback(() => {
    if (recorderRef.current) {
      try {
        recorderRef.current.recorder.stop();
        recorderRef.current.stream?.getTracks?.().forEach((t) => t.stop());
      } catch {}
      recorderRef.current = null;
    }
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setRecordingMessageId(null);
    setRecordingSeconds(0);
  }, []);

  const buildLogText = useCallback(
    (payload) => {
      if (payload?.__reRecord) return '';
      const {
        customer_name,
        services = [],
        chemicals = [],
        transcript = '',
        text = '',
      } = payload;
      const notes = (transcript || text || '').trim();
      const svcNames = services.map((s) => s.name).filter(Boolean).join(', ');
      const chemNames = chemicals.map((c) => c.name).filter(Boolean).join(', ');
      const name = customer_name || intl.formatMessage({ id: 'aiAssistant.quickLog.thisCustomer' });
      const parts = [intl.formatMessage({ id: 'aiAssistant.quickLog.logIntentLine' }, { name })];
      if (svcNames) {
        parts.push(intl.formatMessage({ id: 'aiAssistant.quickLog.servicesLine' }, { names: svcNames }));
      }
      if (chemNames) {
        parts.push(intl.formatMessage({ id: 'aiAssistant.quickLog.chemicalsLine' }, { names: chemNames }));
      }
      if (notes) {
        parts.push(intl.formatMessage({ id: 'aiAssistant.quickLog.notesLine' }, { notes }));
      }
      return parts.join(' ');
    },
    [intl],
  );

  const submitCompose = useCallback(
    async (messageId, payload, flipOptions = {}) => {
      if (role !== 'worker' && role !== 'supervisor') return;
      cancelPreviewTimer(messageId);
      const text = buildLogText(payload);
      if (!text.trim()) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  quickLogError: intl.formatMessage({
                    id: 'aiAssistant.quickLog.error.emptyTranscript',
                    defaultMessage: 'Add a note or pick a pattern.',
                  }),
                }
              : m,
          ),
        );
        return;
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, actionStatus: 'submitting', quickLogError: null } : m,
        ),
      );
      try {
        await sendMessage(text, {
          ...flipOptions,
          silent: true,
          composerCustomerId: payload.customer_id,
        });
        setMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, actionStatus: 'submitted' } : m)),
        );
      } catch {
        setMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, actionStatus: null } : m)),
        );
        throw new Error('submit failed');
      }
    },
    [role, buildLogText, sendMessage, intl, setMessages, cancelPreviewTimer],
  );

  const schedulePreviewSubmit = useCallback(
    (messageId, payload, flipOptions) => {
      cancelPreviewTimer(messageId);
      const tid = setTimeout(() => {
        previewTimersRef.current.delete(messageId);
        submitCompose(messageId, payload, flipOptions).catch(() => {});
      }, PREVIEW_MS);
      previewTimersRef.current.set(messageId, tid);
    },
    [submitCompose, cancelPreviewTimer],
  );

  const stopVoice = useCallback(
    async (messageId) => {
      if (!recorderRef.current || recordingMessageId !== messageId) return;
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setRecordingMessageId(null);
      setRecordingSeconds(0);

      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, actionStatus: 'transcribing', quickLogError: null } : m,
        ),
      );

      try {
        const { blob, mimeType } = await stopRecording(
          recorderRef.current.recorder,
          recorderRef.current.stream,
          recorderRef.current.chunks,
        );
        recorderRef.current = null;

        const ext = (mimeType || '').includes('webm')
          ? 'webm'
          : (mimeType || '').includes('mp4')
            ? 'm4a'
            : 'wav';
        const formData = new FormData();
        formData.append('audio', blob, `voice.${ext}`);
        const baseCtx = { role, ...detectContext() };
        const msg = messagesRef.current?.find((m) => m.id === messageId);
        const cid = msg?.action?.customer_id;
        formData.append(
          'context',
          JSON.stringify(
            cid ? { ...baseCtx, composer_customer_id: String(cid) } : baseCtx,
          ),
        );
        formData.append('displayName', userName || '');
        formData.append('locale', locale || 'en');

        setLoading(true);
        const result = await startStream(formData);
        setLoading(false);

        if (!result) {
          setMessages((prev) =>
            prev.map((m) => (m.id === messageId ? { ...m, actionStatus: null } : m)),
          );
          return;
        }

        const tr = (result.transcript || '').trim();
        if (!tr) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === messageId
                ? {
                    ...m,
                    actionStatus: null,
                    quickLogError: intl.formatMessage({
                      id: 'aiAssistant.quickLog.error.emptyTranscript',
                      defaultMessage: 'Add a note or pick a pattern.',
                    }),
                  }
                : m,
            ),
          );
          return;
        }

        const listId = msg?._sourceListMessageId;
        const flip = listId && cid ? { flipListContext: { listMessageId: listId, customerId: cid } } : {};
        const payload = {
          customer_id: cid,
          customer_name: msg?.action?.customer_name || '',
          transcript: tr,
          services: [],
          chemicals: [],
        };

        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? { ...m, actionStatus: 'preview', quickLogTranscript: tr, quickLogError: null }
              : m,
          ),
        );
        schedulePreviewSubmit(messageId, payload, flip);
      } catch (e) {
        setLoading(false);
        const raw = e instanceof Error ? e.message : String(e ?? '');
        const mic = /notallowed|permission|denied/i.test(raw)
          ? intl.formatMessage({
              id: 'aiAssistant.quickLog.error.micPermission',
              defaultMessage: 'Microphone access denied.',
            })
          : userSafeErrorMessage(e, intl, { fallbackId: 'aiAssistant.quickLog.error.generic', fallbackDefault: "Couldn't log that. Try again." });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId ? { ...m, actionStatus: null, quickLogError: mic } : m,
          ),
        );
      }
    },
    [
      role,
      locale,
      userName,
      detectContext,
      startStream,
      setMessages,
      setLoading,
      intl,
      recordingMessageId,
      messagesRef,
      schedulePreviewSubmit,
    ],
  );

  stopVoiceFnRef.current = stopVoice;

  const startVoice = useCallback(
    async (messageId) => {
      if (role !== 'worker') return;
      try {
        const rec = await startRecording();
        recorderRef.current = rec;
        setRecordingMessageId(messageId);
        setRecordingSeconds(0);
        intervalRef.current = setInterval(() => {
          setRecordingSeconds((prev) => {
            if (prev >= 59) {
              const fn = stopVoiceFnRef.current;
              if (fn) void fn(messageId);
              return prev;
            }
            return prev + 1;
          });
        }, 1000);
      } catch {
        setRecordingMessageId(null);
      }
    },
    [role],
  );

  const onQuickLogReRecord = useCallback(
    (messageId) => {
      cancelPreviewTimer(messageId);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, actionStatus: null, quickLogTranscript: undefined, quickLogError: null }
            : m,
        ),
      );
    },
    [cancelPreviewTimer, setMessages],
  );

  const onQuickLogSubmit = useCallback(
    async (messageId, payload) => {
      if (payload?.__reRecord) {
        onQuickLogReRecord(messageId);
        return;
      }
      const msg = messagesRef.current?.find((m) => m.id === messageId);
      const listId = msg?._sourceListMessageId;
      const cid = payload?.customer_id;
      const flip = listId && cid ? { flipListContext: { listMessageId: listId, customerId: cid } } : {};

      try {
        await submitCompose(messageId, payload, flip);
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  quickLogError: intl.formatMessage({
                    id: 'aiAssistant.quickLog.error.voiceTimeout',
                    defaultMessage: 'Request timed out. Try again.',
                  }),
                }
              : m,
          ),
        );
      }
    },
    [submitCompose, messagesRef, intl, setMessages, onQuickLogReRecord],
  );

  const cancelCompose = useCallback(
    (messageId) => {
      cancelPreviewTimer(messageId);
      cancelVoice();
      const msg = messagesRef.current?.find((m) => m.id === messageId);
      const listId = msg?._sourceListMessageId;
      if (listId) setListRowLock?.(listId, null);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, actionStatus: 'cancelled' } : m,
        ),
      );
    },
    [cancelPreviewTimer, cancelVoice, setMessages, messagesRef, setListRowLock],
  );

  const onQuickLogCancel = useCallback(
    (messageId) => {
      cancelCompose(messageId);
    },
    [cancelCompose],
  );

  const onQuickLogUndo = useCallback(
    (messageId) => {
      const msg = messagesRef.current?.find((m) => m.id === messageId);
      if (!msg || msg.action?.type !== 'quick_log_compose' || msg.actionStatus !== 'cancelled') return;
      cancelPreviewTimer(messageId);
      const listId = msg._sourceListMessageId;
      const cid = msg.action?.customer_id;
      if (listId && cid) setListRowLock?.(listId, cid);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, actionStatus: null } : m,
        ),
      );
    },
    [cancelPreviewTimer, setListRowLock, setMessages, messagesRef],
  );

  const onQuickLogVoiceToggle = useCallback(
    (messageId) => {
      if (recordingMessageId === messageId) {
        void stopVoice(messageId);
      } else {
        if (recordingMessageId) cancelVoice();
        void startVoice(messageId);
      }
    },
    [recordingMessageId, stopVoice, startVoice, cancelVoice],
  );

  return {
    onQuickLogSubmit,
    onQuickLogCancel,
    onQuickLogUndo,
    onQuickLogVoiceToggle,
    quickLogRecordingId: recordingMessageId,
    quickLogRecordingSeconds: recordingSeconds,
  };
}
